// mantine
import {
    Button,
    TagsInput,
    TextInput,
    Textarea,
    UnstyledButton
} from '@mantine/core';
// utils
import { useFormik } from 'formik';
import type {
    TIncomingWebhook,
    TIncomingWebhookCreateData
} from '@newo/api/gateway';
import { processFormData } from '@newo/toolkit/bff';
import {
    useIncomingWebhookCreateMutation,
    useIncomingWebhookUpdateMutation
} from '@/services/webhooks/webhooks.queries.ts';
import validationSchema from '@/pages/Integrations/components/IncomingWebhooks/components/IncomingWebhookForm/IncomingWebhookForm.schema';

interface IncomingWebhookFormProps {
    webhook?: TIncomingWebhook;
    connectorIdn: string;
    onClose?: () => void;
    onSubmit?: (data: TIncomingWebhookCreateData) => void;
}

const IncomingWebhookForm = ({
    webhook,
    connectorIdn,
    onClose,
    onSubmit
}: IncomingWebhookFormProps) => {
    const mutationAdd = useIncomingWebhookCreateMutation();
    const mutationUpdate = useIncomingWebhookUpdateMutation();

    const initialValues: TIncomingWebhookCreateData = {
        idn: webhook?.idn ?? '',
        description: webhook?.description ?? '',
        connector_idn: webhook?.connector_idn ?? connectorIdn,
        event_idns: webhook?.event_idns ?? [],
        allowed_ips: webhook?.allowed_ips ?? []
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: (formData) => {
            const data = processFormData(formData);
            if (webhook) {
                mutationUpdate.mutate(
                    { webhookId: webhook.id, data },
                    {
                        onSuccess() {
                            onSubmit?.(data);
                            onClose?.();
                        },
                        onSettled() {
                            formik.setSubmitting(false);
                        }
                    }
                );
            } else {
                mutationAdd.mutate(data, {
                    onSuccess() {
                        onSubmit?.(data);
                        onClose?.();
                    },
                    onSettled() {
                        formik.setSubmitting(false);
                    }
                });
            }
        }
    });

    const handleCancel = () => {
        onClose?.();
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            {!webhook?.idn && (
                <TextInput
                    withAsterisk
                    error={
                        formik.touched.idn && formik.values.idn
                            ? formik.errors.idn
                            : undefined
                    }
                    label="Idn"
                    name="idn"
                    placeholder="Idn"
                    value={formik.values.idn}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                />
            )}

            <Textarea
                error={
                    formik.touched.description && formik.values.description
                        ? formik.errors.description
                        : undefined
                }
                label="Description"
                name="description"
                placeholder="Description"
                value={formik.values.description}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <TagsInput
                error={formik.errors.event_idns}
                label="Events"
                placeholder="Type and press Enter to add"
                value={formik.values.event_idns}
                onChange={(eventIdns) => {
                    formik.setFieldValue('event_idns', eventIdns);
                }}
            />

            <TagsInput
                error={formik.errors.allowed_ips}
                label="Allowed IPs"
                placeholder="Type and press Enter to add"
                value={formik.values.allowed_ips}
                onChange={(allowedIps) => {
                    formik.setFieldValue('allowed_ips', allowedIps);
                }}
            />

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    {webhook ? 'Update' : 'Create'}
                </Button>
            </div>
        </form>
    );
};

export default IncomingWebhookForm;
